/**
 * Audio Player utility with crossfade support and dual deck system
 */

export class AudioPlayerManager {
  constructor(crossfadeDuration = 5) {
    // Dual deck system like Virtual DJ
    this.deckA = null;
    this.deckB = null;
    this.activeDeck = 'A'; // 'A' or 'B'
    this.currentAudio = null; // Reference to active deck
    this.nextAudio = null;
    this.crossfadeDuration = crossfadeDuration; // seconds (4-6s recommended)
    this.volume = 1.0; // Master volume
    this.deckAVolume = 1.0; // Individual deck A volume
    this.deckBVolume = 1.0; // Individual deck B volume
    this.crossfaderPosition = 0; // -1 (A only) to 1 (B only), 0 = center (both)
    this.crossfadeCurve = 'logarithmic'; // linear, easeIn, easeOut, easeInOut, logarithmic
    this.prefadeStart = 15; // seconds before end to start auto-crossfade (15s for pro feel)
    this.autoPlayMode = 'continuous'; // continuous, stopAfterTrack, loopPlaylist
    this.autoCrossfadeEnabled = true; // Enable automatic crossfade at end of track
    this.autoCrossfadeTriggered = false; // Track if auto-crossfade already triggered for current track
    this.onTrackEnd = null;
    this.onTimeUpdate = null;
    this.onDeckChange = null;
    this.onTrackPreloaded = null; // Callback when track is preloaded on opposite deck
    this.onAutoCrossfade = null; // Callback when auto-crossfade needs to trigger
    this.onDeckStarted = null; // Callback when deck starts playing (for immediate preload)
    this.checkInterval = null;
    this.isCrossfading = false; // Track if crossfade is in progress
    
    // ═══════════════════════════════════════════════════════════════
    // SMART MIX V2: Pitch/Tempo Control & Beat Synchronization
    // ═══════════════════════════════════════════════════════════════
    this.deckAPitchPercent = 0; // Pitch adjustment percentage (-8 to +8)
    this.deckBPitchPercent = 0;
    this.deckABPM = null; // Original BPM of track on deck A
    this.deckBBPM = null; // Original BPM of track on deck B
    this.keyLockEnabled = true; // Preserve pitch when changing tempo (HTML5 Audio limitation: not fully supported)
    
    // ═══════════════════════════════════════════════════════════════
    // LOOP SYSTEM: Beat-synced looping like djay.app
    // ═══════════════════════════════════════════════════════════════
    this.deckALoop = {
      enabled: false,
      size: null, // 1, 2, 4, 8, 16, 32, 64 (beats)
      startTime: null,
      endTime: null,
      loopCount: 0,
      lastJumpTime: null // Prevent duplicate jumps
    };
    this.deckBLoop = {
      enabled: false,
      size: null,
      startTime: null,
      endTime: null,
      loopCount: 0,
      lastJumpTime: null
    };
    
    // ═══════════════════════════════════════════════════════════════
    // LOOP-ROLL SYSTEM: Temporary loops with slip mode (like djay.app)
    // ═══════════════════════════════════════════════════════════════
    this.deckALoopRoll = {
      active: false,
      size: null, // Fractions: 1/32, 1/16, 1/8, 1/4, 1/2, 1, 2, 4, 8, 16, 32
      startTime: null,
      endTime: null,
      virtualTime: null, // Position "réelle" sans la boucle (slip mode)
      activationTime: null // Timestamp d'activation
    };
    this.deckBLoopRoll = {
      active: false,
      size: null,
      startTime: null,
      endTime: null,
      virtualTime: null,
      activationTime: null
    };
    
    // ═══════════════════════════════════════════════════════════════
  }
  /**
   * Initialize audio player with monitoring interval
   */
  init() {
    // AudioPlayer monitoring setup
    
    // Clear any existing interval first
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    
    // Setup monitoring interval for auto-crossfade detection
    let monitoringTick = 0;
    this.checkInterval = setInterval(() => {
      monitoringTick++;
      const activeDeck = this.activeDeck === 'A' ? this.deckA : this.deckB;
      
      // Monitoring tick (logs removed for performance)
      // Removed excessive logging that was running every 500ms
      
      // Check loops on both decks
      this.checkAndApplyLoop('A');
      this.checkAndApplyLoop('B');
      
      if (activeDeck && !activeDeck.paused && activeDeck.duration) {
        const timeRemaining = activeDeck.duration - activeDeck.currentTime;
        
        // Debug log every 5 seconds
        if (Math.floor(activeDeck.currentTime) % 5 === 0) {
          console.log(`⏱️ Active Deck ${this.activeDeck}: ${activeDeck.currentTime.toFixed(1)}s / ${activeDeck.duration.toFixed(1)}s (${timeRemaining.toFixed(1)}s remaining)`);
        }
        
        // ═══════════════════════════════════════════════════════════════
        // AUTO-CROSSFADE DETECTION (15s before end)
        // ═══════════════════════════════════════════════════════════════
        // Check for auto-crossfade trigger with detailed logging
        if (timeRemaining > 0 && timeRemaining <= this.prefadeStart) {
          // Log conditions for debugging
          if (timeRemaining <= 16 && timeRemaining >= 14) {
            // Auto-crossfade check
          }
          
          if (this.autoCrossfadeEnabled && 
              !this.isCrossfading && 
              !this.autoCrossfadeTriggered) {
            
            // Auto-crossfade triggered
            this.autoCrossfadeTriggered = true;
            
            // Notify that auto-crossfade should start
            if (this.onAutoCrossfade) {
              // Calling auto-crossfade callback
              this.onAutoCrossfade();
            } else {
              console.error('❌ CRITICAL: onAutoCrossfade callback not defined!');
            }
          }
        }
      }
    }, 500); // Check every 500ms for precision
    
    // Monitoring interval active
  }

  /**
   * Update player settings
   */
  updateSettings(settings) {
    if (settings.crossfadeDuration !== undefined) {
      this.crossfadeDuration = settings.crossfadeDuration;
    }
    if (settings.crossfadeCurve !== undefined) {
      this.crossfadeCurve = settings.crossfadeCurve;
    }
    if (settings.prefadeStart !== undefined) {
      this.prefadeStart = settings.prefadeStart;
    }
    if (settings.autoPlayMode !== undefined) {
      this.autoPlayMode = settings.autoPlayMode;
    }
  }

  /**
   * Get current settings
   */
  getSettings() {
    return {
      crossfadeDuration: this.crossfadeDuration,
      crossfadeCurve: this.crossfadeCurve,
      prefadeStart: this.prefadeStart,
      autoPlayMode: this.autoPlayMode
    };
  }

  /**
   * Load and play a track on the next available deck
   */
  async play(trackUrl, startPosition = 0) {
    // Determine which deck to use
    const targetDeck = this.activeDeck === 'A' ? 'B' : 'A';
    
    // Stop any existing audio on target deck
    if (targetDeck === 'A' && this.deckA) {
      this.deckA.pause();
    } else if (targetDeck === 'B' && this.deckB) {
      this.deckB.pause();
    }

    // Create new audio element on target deck
    const newAudio = new Audio(trackUrl);
    newAudio.volume = this.volume;
    newAudio.currentTime = startPosition;

    // Assign to deck
    if (targetDeck === 'A') {
      this.deckA = newAudio;
    } else {
      this.deckB = newAudio;
    }

    // Set as current audio
    this.currentAudio = newAudio;
    this.activeDeck = targetDeck;

    // Add event listeners
    newAudio.addEventListener('ended', () => {
      if (this.onTrackEnd) {
        this.onTrackEnd();
      }
    });

    newAudio.addEventListener('timeupdate', () => {
      if (this.onTimeUpdate) {
        this.onTimeUpdate({
          currentTime: newAudio.currentTime,
          duration: newAudio.duration,
          activeDeck: this.activeDeck
        });
      }

      // Check for crossfade
      this.checkForCrossfade();
    });

    // Notify deck change
    if (this.onDeckChange) {
      this.onDeckChange({
        activeDeck: this.activeDeck,
        deckAPlaying: this.deckA && !this.deckA.paused,
        deckBPlaying: this.deckB && !this.deckB.paused
      });
    }

    // Play
    try {
      await newAudio.play();
      return true;
    } catch (error) {
      console.error('Error playing audio:', error);
      return false;
    }
  }

  /**
   * Pause current track with fade-out
   */
  async pause(fadeDuration = 1000) {
    if (this.currentAudio) {
      const audio = this.currentAudio;
      const startVolume = audio.volume;
      const fadeSteps = 20;
      const stepTime = fadeDuration / fadeSteps;
      const volumeStep = startVolume / fadeSteps;
      
      // Fade out
      for (let i = 0; i < fadeSteps; i++) {
        audio.volume = Math.max(0, startVolume - (volumeStep * i));
        await new Promise(resolve => setTimeout(resolve, stepTime));
      }
      
      // Pause and restore volume
      audio.pause();
      audio.volume = startVolume;
    }
  }
  
  /**
   * Pause a specific deck with fade-out
   */
  async pauseDeck(deckId, fadeDuration = 1000) {
    const audio = deckId === 'A' ? this.deckA : this.deckB;
    if (audio) {
      const startVolume = audio.volume;
      const fadeSteps = 20;
      const stepTime = fadeDuration / fadeSteps;
      const volumeStep = startVolume / fadeSteps;
      
      // Fade out
      for (let i = 0; i < fadeSteps; i++) {
        audio.volume = Math.max(0, startVolume - (volumeStep * i));
        await new Promise(resolve => setTimeout(resolve, stepTime));
      }
      
      // Pause and restore volume
      audio.pause();
      audio.volume = startVolume;
    }
  }

  /**
   * Resume playback
   */
  resume() {
    if (this.currentAudio) {
      this.currentAudio.play();
    }
  }
  
  /**
   * Play a specific deck (manual mode)
   */
  async playDeck(deckId) {
    const audio = deckId === 'A' ? this.deckA : this.deckB;
    if (audio && audio.paused) {
      try {
        await audio.play();
        console.log(`▶️ Deck ${deckId} started playing`);
        return true;
      } catch (error) {
        console.error(`Error playing Deck ${deckId}:`, error);
        return false;
      }
    }
    return false;
  }
  
  /**
   * Toggle play/pause for a specific deck (manual mode)
   */
  async toggleDeck(deckId) {
    const audio = deckId === 'A' ? this.deckA : this.deckB;
    if (audio) {
      if (audio.paused) {
        await this.playDeck(deckId);
      } else {
        await this.pauseDeck(deckId);
      }
    }
  }

  /**
   * Seek to position
   */
  seek(position) {
    if (this.currentAudio) {
      this.currentAudio.currentTime = position;
    }
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    this.updateAllVolumes();
  }

  /**
   * Enable or disable auto-crossfade at end of tracks
   */
  setAutoCrossfade(enabled) {
    this.autoCrossfadeEnabled = enabled;
    console.log(`🎚️ Auto-crossfade ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Set prefade start time (seconds before end to start crossfade)
   */
  setPrefadeStart(seconds) {
    this.prefadeStart = Math.max(1, Math.min(10, seconds));
    console.log(`⏰ Prefade start set to ${this.prefadeStart}s`);
  }

  /**
   * Set individual deck volume (0.0 to 1.0)
   */
  setDeckVolume(deckId, vol) {
    const volume = Math.max(0, Math.min(1, vol));
    console.log(`🔊 Setting Deck ${deckId} volume: ${Math.round(volume * 100)}%`);
    
    if (deckId === 'A') {
      this.deckAVolume = volume;
    } else if (deckId === 'B') {
      this.deckBVolume = volume;
    }
    
    this.updateAllVolumes();
  }

  /**
   * Get deck volume
   */
  getDeckVolume(deckId) {
    return deckId === 'A' ? this.deckAVolume : this.deckBVolume;
  }

  /**
   * Update all deck volumes based on master, deck, and crossfader
   */
  updateAllVolumes() {
    this.updateCrossfaderVolumes();
  }

  /**
   * Set crossfader position (-1 to 1) with smooth transition
   * -1 = Deck A only, 0 = Both equal, 1 = Deck B only
   */
  setCrossfader(position, smooth = true) {
    const targetPosition = Math.max(-1, Math.min(1, position));
    
    if (!smooth || !this.deckA || !this.deckB) {
      // Immediate change
      this.crossfaderPosition = targetPosition;
      this.updateCrossfaderVolumes();
      return;
    }

    // Smooth transition
    const startPosition = this.crossfaderPosition;
    const steps = 20;
    const stepDuration = 50; // ms
    let currentStep = 0;

    const fadeInterval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      // Smooth easing
      const easedProgress = this.calculateCrossfadeVolume(progress, 'easeInOut');
      this.crossfaderPosition = startPosition + (targetPosition - startPosition) * easedProgress;
      
      this.updateCrossfaderVolumes();

      if (currentStep >= steps) {
        clearInterval(fadeInterval);
        this.crossfaderPosition = targetPosition;
        this.updateCrossfaderVolumes();
      }
    }, stepDuration);
  }

  /**
   * Update volumes based on current crossfader position
   * PHASE 4: Uses Web Audio API gain nodes when available
   * Volume formula: finalVolume = masterVolume * deckVolume * crossfaderCurve
   */
  updateCrossfaderVolumes() {
    // Use logarithmic curve for natural sound perception
    const posA = Math.max(0, -this.crossfaderPosition); // 0 to 1 when fader is left
    const posB = Math.max(0, this.crossfaderPosition);  // 0 to 1 when fader is right
    
    // Logarithmic curve for smooth, natural crossfade
    const crossfadeA = this.calculateCrossfadeVolume(1 - posB, 'logarithmic');
    const crossfadeB = this.calculateCrossfadeVolume(1 - posA, 'logarithmic');
    
    // Final volume = master * deck individual * crossfader curve
    const volumeA = this.volume * this.deckAVolume * crossfadeA;
    const volumeB = this.volume * this.deckBVolume * crossfadeB;

    // Apply to HTML5 Audio (used by crossfade system)
    if (this.deckA) {
      this.deckA.volume = Math.max(0, Math.min(1, volumeA));
    }
    if (this.deckB) {
      this.deckB.volume = Math.max(0, Math.min(1, volumeB));
    }
  }

  /**
   * Get deck info
   */
  getDecksInfo() {
    return {
      deckA: this.deckA ? {
        currentTime: this.deckA.currentTime,
        duration: this.deckA.duration || 0,
        paused: this.deckA.paused,
        volume: this.deckA.volume
      } : null,
      deckB: this.deckB ? {
        currentTime: this.deckB.currentTime,
        duration: this.deckB.duration || 0,
        paused: this.deckB.paused,
        volume: this.deckB.volume
      } : null,
      activeDeck: this.activeDeck,
      crossfaderPosition: this.crossfaderPosition
    };
  }

  /**
   * Get current playback info
   */
  getCurrentInfo() {
    if (!this.currentAudio) {
      return { currentTime: 0, duration: 0, paused: true };
    }
    return {
      currentTime: this.currentAudio.currentTime,
      duration: this.currentAudio.duration || 0,
      paused: this.currentAudio.paused
    };
  }

  /**
   * Preload next track for crossfade
   */
  preloadNext(trackUrl) {
    this.nextAudio = new Audio(trackUrl);
    this.nextAudio.volume = 0;
    this.nextAudio.load();
  }

  /**
   * Check if crossfade should start
   */
  checkForCrossfade() {
    if (!this.currentAudio || !this.nextAudio) return;

    const timeRemaining = this.currentAudio.duration - this.currentAudio.currentTime;

    // Start crossfade when crossfadeDuration seconds remain
    if (timeRemaining <= this.crossfadeDuration && timeRemaining > 0) {
      this.performCrossfade(timeRemaining);
    }
  }

  /**
   * Calculate volume based on crossfade curve
   */
  calculateCrossfadeVolume(progress, curve) {
    // progress: 0 to 1
    switch (curve) {
      case 'linear':
        return progress;
      
      case 'easeIn':
        return progress * progress;
      
      case 'easeOut':
        return 1 - (1 - progress) * (1 - progress);
      
      case 'easeInOut':
        return progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      case 'logarithmic':
        // Logarithmic curve for natural sound perception
        return Math.log10(1 + progress * 9) / Math.log10(10);
      
      default:
        return progress;
    }
  }

  /**
   * Perform crossfade between current and next track
   */
  async performCrossfade(duration) {
    if (!this.nextAudio || this.nextAudio.volume > 0) return;

    const steps = 60; // More steps for smoother transition
    const interval = (duration * 1000) / steps;

    // Start playing next track
    try {
      await this.nextAudio.play();
    } catch (error) {
      console.error('Error starting next track:', error);
      return;
    }

    // Gradually fade with curve
    let step = 0;
    const fadeInterval = setInterval(() => {
      step++;
      const progress = step / steps;
      
      // Calculate volumes using selected curve
      const fadeInVolume = this.calculateCrossfadeVolume(progress, this.crossfadeCurve);
      const fadeOutVolume = this.calculateCrossfadeVolume(1 - progress, this.crossfadeCurve);
      
      if (this.currentAudio) {
        this.currentAudio.volume = Math.max(0, this.volume * fadeOutVolume);
      }
      
      if (this.nextAudio) {
        this.nextAudio.volume = Math.min(this.volume, this.volume * fadeInVolume);
      }

      if (step >= steps) {
        clearInterval(fadeInterval);
        
        // Swap audio elements
        if (this.currentAudio) {
          this.currentAudio.pause();
        }
        this.currentAudio = this.nextAudio;
        this.nextAudio = null;
        
        // Re-attach event listeners to new current audio
        if (this.currentAudio) {
          this.currentAudio.addEventListener('ended', () => {
            if (this.onTrackEnd) {
              this.onTrackEnd();
            }
          });
          
          this.currentAudio.addEventListener('timeupdate', () => {
            if (this.onTimeUpdate) {
              this.onTimeUpdate({
                currentTime: this.currentAudio.currentTime,
                duration: this.currentAudio.duration
              });
            }
            this.checkForCrossfade();
          });
        }
      }
    }, interval);
  }

  /**
   * Immediately transition to next track
   */
  async crossfadeNow(nextTrackUrl) {
    if (!this.currentAudio) {
      return this.play(nextTrackUrl);
    }

    // Create next audio
    const nextAudio = new Audio(nextTrackUrl);
    nextAudio.volume = 0;

    try {
      await nextAudio.play();
    } catch (error) {
      console.error('Error playing next track:', error);
      return false;
    }

    // Quick crossfade (2 seconds)
    const steps = 40;
    const interval = 50; // ms
    const volumeStep = this.volume / steps;

    let step = 0;
    const fadeInterval = setInterval(() => {
      step++;
      
      if (this.currentAudio) {
        this.currentAudio.volume = Math.max(0, this.volume - (volumeStep * step));
      }
      nextAudio.volume = Math.min(this.volume, volumeStep * step);

      if (step >= steps) {
        clearInterval(fadeInterval);
        
        if (this.currentAudio) {
          this.currentAudio.pause();
        }
        this.currentAudio = nextAudio;
        
        // Attach event listeners
        this.currentAudio.addEventListener('ended', () => {
          if (this.onTrackEnd) {
            this.onTrackEnd();
          }
        });
        
        this.currentAudio.addEventListener('timeupdate', () => {
          if (this.onTimeUpdate) {
            this.onTimeUpdate({
              currentTime: this.currentAudio.currentTime,
              duration: this.currentAudio.duration
            });
          }
          this.checkForCrossfade();
        });
      }
    }, interval);

    return true;
  }

  /**
   * Stop playback and cleanup
   */
  stop() {
    if (this.deckA) {
      this.deckA.pause();
      this.deckA = null;
    }
    if (this.deckB) {
      this.deckB.pause();
      this.deckB = null;
    }
    if (this.nextAudio) {
      this.nextAudio.pause();
      this.nextAudio = null;
    }
    this.currentAudio = null;
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  /**
   * Load a track on a specific deck without playing
   */
  loadTrackOnDeck(deckId, trackUrl, trackBPM = null) {
    // Loading track
    
    const audio = new Audio(trackUrl);
    audio.volume = 0; // Start silent, crossfader will control volume
    
    // ═══════════════════════════════════════════════════════════════
    // SMART MIX V2: Store BPM and apply current pitch setting
    // ═══════════════════════════════════════════════════════════════
    if (trackBPM) {
      if (deckId === 'A') {
        this.deckABPM = trackBPM;
      } else {
        this.deckBBPM = trackBPM;
      }
    }
    
    // Apply current pitch setting to new track
    const pitchPercent = deckId === 'A' ? this.deckAPitchPercent : this.deckBPitchPercent;
    const playbackRate = 1.0 + (pitchPercent / 100);
    audio.playbackRate = playbackRate;
    // Playback rate set
    
    audio.load();
    
    // Setup event listeners
    audio.addEventListener('ended', () => {
      // Track ended
      if (this.onTrackEnd) {
        this.onTrackEnd(deckId);
      }
    });
    
    audio.addEventListener('loadedmetadata', () => {
      if (audio.duration && audio.duration > 0 && audio.duration !== Infinity) {
        // Pre-position at 8s on load for intro skip
        if (audio.duration > 30) {
          audio.currentTime = 8;
          // Track loaded with intro skip
        } else {
          // Track loaded
        }
      }
    });
    
    audio.addEventListener('timeupdate', () => {
      const timeRemaining = audio.duration - audio.currentTime;
      
      // Check loop-roll FIRST (has priority over regular loop)
      this.checkAndApplyLoopRoll(deckId);
      
      // Check regular loop
      this.checkAndApplyLoop(deckId);
      
      if (this.onTimeUpdate) {
        this.onTimeUpdate({
          currentTime: audio.currentTime,
          duration: audio.duration,
          deckId: deckId,
          paused: audio.paused,
          timeRemaining: timeRemaining
        });
      }
    });
    
    // Store on appropriate deck
    if (deckId === 'A') {
      if (this.deckA) {
        this.deckA.pause();
      }
      this.deckA = audio;
      // Set deck volume to 0 for UI display (track not playing yet)
      if (deckId !== this.activeDeck) {
        this.deckAVolume = 0;
      }
    } else {
      if (this.deckB) {
        this.deckB.pause();
      }
      this.deckB = audio;
      // Set deck volume to 0 for UI display (track not playing yet)
      if (deckId !== this.activeDeck) {
        this.deckBVolume = 0;
      }
    }
    
    // Reset auto-crossfade flag when loading new track
    // Important: Reset regardless of which deck, because after crossfade
    // the new track becomes active and needs the flag reset
    this.autoCrossfadeTriggered = false;
    console.log(`🔄 Auto-crossfade flag reset for Deck ${deckId}`);
    
    // ═══════════════════════════════════════════════════════════════
    // CRITICAL FIX: Do NOT call updateCrossfaderVolumes() here!
    // ═══════════════════════════════════════════════════════════════
    // Calling updateCrossfaderVolumes() here recalculates ALL deck volumes
    // based on crossfader position, which can cause a momentary volume drop
    // on the currently playing deck when loading a track on the other deck.
    // 
    // The newly loaded track is already at volume=0 (line 562), which is correct.
    // Volume adjustments should only happen during the actual crossfade,
    // not during preload/loading operations.
    //
    // REMOVED: this.updateCrossfaderVolumes();
    // ═══════════════════════════════════════════════════════════════
    
    // Notify deck change
    if (this.onDeckChange) {
      this.onDeckChange(this.getDecksInfo());
    }
    
    return audio;
  }

  /**
   * Play/Pause a specific deck
   */
  async playPauseDeck(deckId) {
    console.log(`⏯️ Play/Pause Deck ${deckId}`);
    
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck) {
      console.warn(`⚠️ No track loaded on Deck ${deckId}`);
      return false;
    }
    
    const wasPlaying = !deck.paused;
    
    if (deck.paused) {
      // Play
      try {
        // Track is already positioned at 8s from loadTrackOnDeck
        await deck.play();
        this.currentAudio = deck;
        this.activeDeck = deckId;
        
        // ═══════════════════════════════════════════════════════════════
        // CRITICAL FIX: Restore deck volume to 1.0 before updating
        // Without this, Web Audio gain nodes stay at 0, causing silence
        // ═══════════════════════════════════════════════════════════════
        if (deckId === 'A') {
          this.deckAVolume = 1.0;
        } else {
          this.deckBVolume = 1.0;
        }
        
        // Playing deck
        
        // Update volumes (will now calculate correctly with deckVolume = 1.0)
        this.updateCrossfaderVolumes();
        
        // Notify deck change
        if (this.onDeckChange) {
          this.onDeckChange(this.getDecksInfo());
        }
        
        // CRITICAL: Notify that deck started playing (for immediate preload on opposite)
        if (!wasPlaying && this.onDeckStarted) {
          this.onDeckStarted(deckId);
        }
        
        return true;
      } catch (error) {
        console.error(`❌ Error playing Deck ${deckId}:`, error);
        return false;
      }
    } else {
      // Pause
      deck.pause();
      console.log(`⏸️ Paused Deck ${deckId}`);
      
      // Notify deck change
      if (this.onDeckChange) {
        this.onDeckChange(this.getDecksInfo());
      }
      
      return true;
    }
  }

  /**
   * CUE: Return to start of track on specific deck (MVP version)
   */
  cueDeck(deckId) {
    console.log(`🔄 CUE Deck ${deckId} - Return to start`);
    
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck) {
      console.warn(`⚠️ No track loaded on Deck ${deckId}`);
      return false;
    }
    
    // Pause and return to beginning
    const wasPlaying = !deck.paused;
    deck.pause();
    deck.currentTime = 0;
    
    // If it was playing, resume
    if (wasPlaying) {
      deck.play();
    }
    
    console.log(`✅ CUE Deck ${deckId} complete`);
    return true;
  }

  /**
   * Seek a specific deck to a position
   */
  seekDeck(deckId, position) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (!deck) {
      console.warn(`⚠️ No track loaded on Deck ${deckId}`);
      return false;
    }
    
    deck.currentTime = Math.max(0, Math.min(position, deck.duration || 0));
    console.log(`⏩ Seek Deck ${deckId} to ${position.toFixed(2)}s`);
    return true;
  }

  /**
   * PHASE 5: Set hot cue for a deck
   */
  setHotCue(deckId, index, time, label = '', color = '#FF0000') {
    const cue = { index, time, label, color };
    
    if (deckId === 'A') {
      // Remove existing cue at this index
      this.deckAHotCues = this.deckAHotCues.filter(c => c.index !== index);
      this.deckAHotCues.push(cue);
      this.deckAHotCues.sort((a, b) => a.index - b.index);
    } else {
      this.deckBHotCues = this.deckBHotCues.filter(c => c.index !== index);
      this.deckBHotCues.push(cue);
      this.deckBHotCues.sort((a, b) => a.index - b.index);
    }
    
    return cue;
  }

  /**
   * PHASE 5: Jump to hot cue
   */
  jumpToHotCue(deckId, index) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const cues = deckId === 'A' ? this.deckAHotCues : this.deckBHotCues;
    
    if (!deck) return false;
    
    const cue = cues.find(c => c.index === index);
    if (!cue) return false;
    
    deck.currentTime = cue.time;
    return true;
  }

  /**
   * PHASE 5: Delete hot cue
   */
  deleteHotCue(deckId, index) {
    if (deckId === 'A') {
      this.deckAHotCues = this.deckAHotCues.filter(c => c.index !== index);
    } else {
      this.deckBHotCues = this.deckBHotCues.filter(c => c.index !== index);
    }
  }

  /**
   * SMART MIX V2: Set pitch/tempo for a specific deck
   * @param {string} deckId - 'A' or 'B'
   * @param {number} pitchPercent - Pitch adjustment percentage (-8 to +8)
   */
  setPitchDeck(deckId, pitchPercent) {
    // Clamp pitch to range
    const clampedPitch = Math.max(-8, Math.min(8, pitchPercent));
    
    // Setting pitch
    
    // Store pitch setting
    if (deckId === 'A') {
      this.deckAPitchPercent = clampedPitch;
    } else {
      this.deckBPitchPercent = clampedPitch;
    }
    
    // Calculate playback rate (1.0 = normal speed)
    // +8% pitch = 1.08x speed, -8% pitch = 0.92x speed
    const playbackRate = 1.0 + (clampedPitch / 100);
    
    // Apply to deck audio if loaded
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    if (deck) {
      deck.playbackRate = playbackRate;
      
      // Calculate current BPM
      const originalBPM = deckId === 'A' ? this.deckABPM : this.deckBBPM;
      const currentBPM = originalBPM ? (originalBPM * playbackRate).toFixed(1) : null;
      
      // Playback rate applied
      
      // Notify UI of change
      if (this.onDeckChange) {
        this.onDeckChange(this.getDecksInfo());
      }
    } else {
      // Deck not loaded
    }
    
    return playbackRate;
  }

  /**
   * SMART MIX V2: Get pitch information for a deck
   */
  getPitchInfo(deckId) {
    const pitchPercent = deckId === 'A' ? this.deckAPitchPercent : this.deckBPitchPercent;
    const originalBPM = deckId === 'A' ? this.deckABPM : this.deckBBPM;
    const playbackRate = 1.0 + (pitchPercent / 100);
    const currentBPM = originalBPM ? (originalBPM * playbackRate).toFixed(1) : null;
    
    return {
      pitchPercent,
      playbackRate,
      originalBPM,
      currentBPM
    };
  }

  /**
   * SMART MIX V2: Synchronize deck BPMs
   * Adjusts target deck's pitch to match source deck's current BPM
   */
  syncDecks() {
    const sourceDeck = this.activeDeck;
    const targetDeck = sourceDeck === 'A' ? 'B' : 'A';
    
    const sourceBPM = sourceDeck === 'A' ? this.deckABPM : this.deckBBPM;
    const targetBPM = targetDeck === 'A' ? this.deckABPM : this.deckBBPM;
    const sourcePlaybackRate = 1.0 + ((sourceDeck === 'A' ? this.deckAPitchPercent : this.deckBPitchPercent) / 100);
    
    // Sync decks
    console.log(`   Source: ${sourceBPM} BPM * ${sourcePlaybackRate.toFixed(3)} = ${(sourceBPM * sourcePlaybackRate).toFixed(1)} BPM`);
    console.log(`   Target: ${targetBPM} BPM (original)`);
    
    if (!sourceBPM || !targetBPM) {
      console.error('❌ Cannot sync: BPM data missing');
      return {
        success: false,
        message: 'BPM data missing for one or both tracks'
      };
    }
    
    // Calculate required pitch adjustment for target deck
    // targetBPM * targetRate = sourceBPM * sourceRate
    // targetRate = (sourceBPM * sourceRate) / targetBPM
    const sourceCurrentBPM = sourceBPM * sourcePlaybackRate;
    const requiredPlaybackRate = sourceCurrentBPM / targetBPM;
    const requiredPitchPercent = (requiredPlaybackRate - 1.0) * 100;
    
    // Check if within pitch range (-8% to +8%)
    if (requiredPitchPercent < -8 || requiredPitchPercent > 8) {
      console.warn(`⚠️ Required pitch ${requiredPitchPercent.toFixed(1)}% exceeds range (-8% to +8%)`);
      // Clamp to range
      const clampedPitch = Math.max(-8, Math.min(8, requiredPitchPercent));
      this.setPitchDeck(targetDeck, clampedPitch);
      return {
        success: false,
        message: `BPM difference too large (clamped to ${clampedPitch > 0 ? '+' : ''}${clampedPitch.toFixed(1)}%)`,
        actualPitch: clampedPitch
      };
    }
    
    // Apply pitch to target deck
    this.setPitchDeck(targetDeck, requiredPitchPercent);
    
    // Sync complete
    
    return {
      success: true,
      message: `Deck ${targetDeck} synchronized to ${sourceCurrentBPM.toFixed(1)} BPM`,
      pitchApplied: requiredPitchPercent
    };
  }

  // ═══════════════════════════════════════════════════════════════
  // LOOP SYSTEM METHODS
  // ═══════════════════════════════════════════════════════════════
  
  /**
   * Set loop for a specific deck with PROFESSIONAL QUANTIZATION
   * @param {string} deckId - 'A' or 'B'
   * @param {number|null} size - Loop size in beats (1, 2, 4, 8, 16, 32, 64) or null to disable
   */
  setLoop(deckId, size) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const loopState = deckId === 'A' ? this.deckALoop : this.deckBLoop;
    
    if (!deck || !deck.src) {
      console.warn(`⚠️ Cannot set loop: Deck ${deckId} has no track loaded`);
      return false;
    }
    
    if (size === null) {
      // Disable loop
      loopState.enabled = false;
      loopState.size = null;
      loopState.startTime = null;
      loopState.endTime = null;
      loopState.loopCount = 0;
      loopState.lastJumpTime = null;
      console.log(`🔄 Loop disabled on Deck ${deckId}`);
      return true;
    }
    
    // Calculate loop duration based on BPM
    const bpm = deckId === 'A' ? this.deckABPM : this.deckBBPM;
    
    if (!bpm) {
      console.warn(`⚠️ Cannot set loop: No BPM detected for Deck ${deckId}`);
      // Fallback: use time-based loop (1 beat ≈ 0.5s at 120 BPM)
      const fallbackBPM = 120;
      const secondsPerBeat = 60 / fallbackBPM;
      const currentBeat = Math.floor(deck.currentTime / secondsPerBeat);
      const loopDuration = size * secondsPerBeat;
      
      loopState.enabled = true;
      loopState.size = size;
      loopState.startTime = currentBeat * secondsPerBeat;
      loopState.endTime = loopState.startTime + loopDuration;
      loopState.loopCount = 0;
      
      console.log(`🔁 Loop set on Deck ${deckId}: ${size} beats (${loopDuration.toFixed(2)}s) [NO BPM - quantized at 120 BPM]`);
      return true;
    }
    
    // ═══════════════════════════════════════════════════════════════
    // PROFESSIONAL QUANTIZATION: Like djay.app
    // Loop starts from CURRENT position (quantized) and repeats
    // ═══════════════════════════════════════════════════════════════
    
    const secondsPerBeat = 60 / bpm;
    
    // Get current position in beats
    const currentBeat = deck.currentTime / secondsPerBeat;
    
    // QUANTIZE START: Round DOWN to current beat (not up, to avoid jumping forward)
    // This ensures the loop starts where we are musically
    const quantizedStartBeat = Math.floor(currentBeat);
    
    // Calculate loop end based on size
    const loopEndBeat = quantizedStartBeat + size;
    
    // Convert back to seconds
    const startTime = quantizedStartBeat * secondsPerBeat;
    const endTime = loopEndBeat * secondsPerBeat;
    
    // Ensure loop is within track duration
    if (endTime > deck.duration) {
      console.warn(`⚠️ Loop would exceed track duration (${endTime.toFixed(2)}s > ${deck.duration.toFixed(2)}s)`);
      
      // Adjust: move start backward to fit the loop
      const maxStartBeat = Math.floor((deck.duration / secondsPerBeat) - size);
      
      if (maxStartBeat < 0) {
        console.error(`❌ Track too short for ${size}-beat loop`);
        return false;
      }
      
      loopState.startTime = maxStartBeat * secondsPerBeat;
      loopState.endTime = loopState.startTime + (size * secondsPerBeat);
      
      // If we're past the adjusted loop, jump to loop start
      if (deck.currentTime > loopState.startTime) {
        deck.currentTime = loopState.startTime;
      }
    } else {
      loopState.startTime = startTime;
      loopState.endTime = endTime;
      
      // NO JUMP: Let the track continue playing naturally
      // The loop will kick in when we reach the end
    }
    
    // Set loop state
    loopState.enabled = true;
    loopState.size = size;
    loopState.loopCount = 0;
    
    const loopDuration = loopState.endTime - loopState.startTime;
    console.log(`🔁 Loop ON - Deck ${deckId}: ${size} beats (${loopDuration.toFixed(3)}s) | Start: beat ${quantizedStartBeat} (${loopState.startTime.toFixed(3)}s) → End: beat ${loopEndBeat} (${loopState.endTime.toFixed(3)}s)`);
    
    return true;
  }
  
  /**
   * Check and apply loop for a deck during playback
   * Called from timeupdate event (~every 250ms) for maximum precision
   * Uses tight timing window for seamless loops like djay.app
   */
  checkAndApplyLoop(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const loopState = deckId === 'A' ? this.deckALoop : this.deckBLoop;
    
    if (!loopState.enabled || !deck || !deck.duration || deck.paused) {
      return;
    }
    
    // CRITICAL: Detect when we're approaching or past the loop end
    // timeupdate fires ~every 250ms, so we need to catch it in that window
    const timeUntilEnd = loopState.endTime - deck.currentTime;
    
    // Jump back if:
    // 1. We've passed the end point
    // 2. We're within 300ms of the end (to catch it before the next timeupdate)
    if (timeUntilEnd <= 0.3 && timeUntilEnd >= -0.5) {
      // Prevent multiple jumps in same timeupdate cycle
      if (loopState.lastJumpTime && (performance.now() - loopState.lastJumpTime) < 200) {
        return; // Too soon, skip
      }
      
      // SEAMLESS JUMP to start
      const previousTime = deck.currentTime;
      deck.currentTime = loopState.startTime;
      loopState.lastJumpTime = performance.now();
      
      // Increment counter
      loopState.loopCount = (loopState.loopCount || 0) + 1;
      
      // Debug log (only first 3 loops then every 5th to reduce spam)
      if (loopState.loopCount <= 3 || loopState.loopCount % 5 === 0) {
        console.log(`🔁 Loop #${loopState.loopCount} - Deck ${deckId}: ${previousTime.toFixed(3)}s → ${loopState.startTime.toFixed(3)}s (${loopState.size} beats)`);
      }
    }
  }
  
  /**
   * Get current loop state for a deck
   */
  getLoopState(deckId) {
    return deckId === 'A' ? this.deckALoop : this.deckBLoop;
  }

  // ═══════════════════════════════════════════════════════════════
  // LOOP-ROLL METHODS (Temporary loops with slip mode)
  // ═══════════════════════════════════════════════════════════════
  
  /**
   * Activate loop-roll (temporary loop) - HOLD button
   * @param {string} deckId - 'A' or 'B'
   * @param {number} size - Loop size (can be fractional: 0.03125 for 1/32, 0.0625 for 1/16, etc.)
   */
  activateLoopRoll(deckId, size) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const loopRollState = deckId === 'A' ? this.deckALoopRoll : this.deckBLoopRoll;
    const bpm = deckId === 'A' ? this.deckABPM : this.deckBBPM;
    
    if (!deck || !deck.src || deck.paused) {
      console.warn(`⚠️ Cannot activate loop-roll: Deck ${deckId} not playing`);
      return false;
    }
    
    // If already active, don't reactivate
    if (loopRollState.active) {
      return false;
    }
    
    const fallbackBPM = bpm || 120;
    const secondsPerBeat = 60 / fallbackBPM;
    
    // Calculate loop duration
    const loopDuration = size * secondsPerBeat;
    
    // Quantize start to current beat (floor)
    const currentBeat = Math.floor(deck.currentTime / secondsPerBeat);
    const startTime = currentBeat * secondsPerBeat;
    const endTime = startTime + loopDuration;
    
    // Activate loop-roll
    loopRollState.active = true;
    loopRollState.size = size;
    loopRollState.startTime = startTime;
    loopRollState.endTime = endTime;
    loopRollState.virtualTime = deck.currentTime; // Save "real" position
    loopRollState.activationTime = performance.now();
    
    // Jump to loop start
    deck.currentTime = startTime;
    
    console.log(`🎚️ LOOP-ROLL ON - Deck ${deckId}: ${size} beats (${loopDuration.toFixed(3)}s) | Slip mode: virtual position saved`);
    
    return true;
  }
  
  /**
   * Deactivate loop-roll - RELEASE button
   * Returns to the "real" position (slip mode)
   */
  deactivateLoopRoll(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const loopRollState = deckId === 'A' ? this.deckALoopRoll : this.deckBLoopRoll;
    
    if (!loopRollState.active || !deck) {
      return false;
    }
    
    // Calculate elapsed time since activation
    const elapsedMs = performance.now() - loopRollState.activationTime;
    const elapsedSeconds = elapsedMs / 1000;
    
    // Calculate "real" position (slip mode)
    const realPosition = loopRollState.virtualTime + elapsedSeconds;
    
    // Jump to real position
    deck.currentTime = Math.min(realPosition, deck.duration);
    
    console.log(`🎚️ LOOP-ROLL OFF - Deck ${deckId}: Jumped to real position ${realPosition.toFixed(3)}s (slip mode)`);
    
    // Reset state
    loopRollState.active = false;
    loopRollState.size = null;
    loopRollState.startTime = null;
    loopRollState.endTime = null;
    loopRollState.virtualTime = null;
    loopRollState.activationTime = null;
    
    return true;
  }
  
  /**
   * Check and apply loop-roll during playback
   */
  checkAndApplyLoopRoll(deckId) {
    const deck = deckId === 'A' ? this.deckA : this.deckB;
    const loopRollState = deckId === 'A' ? this.deckALoopRoll : this.deckBLoopRoll;
    
    if (!loopRollState.active || !deck || deck.paused) {
      return;
    }
    
    // Check if we need to loop back
    const timeUntilEnd = loopRollState.endTime - deck.currentTime;
    
    if (timeUntilEnd <= 0.3 && timeUntilEnd >= -0.5) {
      // Jump back to start
      deck.currentTime = loopRollState.startTime;
    }
  }

  /**
   * Crossfade from current deck to the opposite deck with next track
   * Implements progressive volume fade: outgoing track fades out, incoming fades in
   * After crossfade, automatically preloads next track on now-free deck
   */
  async crossfadeToNext(nextTrackUrl, queueForPreload = [], nextTrackBPM = null) {
    const tCrossStart = performance.now();
    
    if (this.isCrossfading) {
      console.warn('⚠️ Crossfade already in progress, ignoring request');
      return false;
    }
    
    this.isCrossfading = true;
    this.autoCrossfadeTriggered = false; // Reset for next track
    // Crossfade starting
    
    // Determine target deck (opposite of current)
    const sourceDeck = this.activeDeck;
    const targetDeck = this.activeDeck === 'A' ? 'B' : 'A';
    
    const t1 = performance.now();
    // Crossfade deck switch
    
    // ═══════════════════════════════════════════════════════════════
    // CRITICAL: Check if track is ALREADY loaded on target deck
    // ═══════════════════════════════════════════════════════════════
    const sourceAudio = sourceDeck === 'A' ? this.deckA : this.deckB;
    let targetAudio = targetDeck === 'A' ? this.deckA : this.deckB;
    
    // Check if target deck already has the right track loaded
    const targetTrackAlreadyLoaded = targetAudio && targetAudio.src === nextTrackUrl;
    
    if (targetTrackAlreadyLoaded) {
      console.log(`✅ Target Deck ${targetDeck} already has correct track loaded - REUSING (no reload)`);
      // Reset to 8s (intro skip) for professional DJ mixing
      const introSkipSeconds = targetAudio.duration > 30 ? 8 : 0;
      targetAudio.currentTime = introSkipSeconds;
      console.log(`🔄 Reset target deck to ${introSkipSeconds}s (intro skip)`);
      // Ensure target deck volume is 0 before crossfade
      targetAudio.volume = 0;
    } else {
      // ═══════════════════════════════════════════════════════════════
      // CRITICAL: Preserve source deck volume before loading new track
      // ═══════════════════════════════════════════════════════════════
      const sourceVolumeBeforeLoad = sourceAudio ? sourceAudio.volume : 1.0;
      console.log(`🔒 Locking source Deck ${sourceDeck} volume at ${Math.round(sourceVolumeBeforeLoad * 100)}% during load`);
      
      // Load next track on target deck with BPM
      this.loadTrackOnDeck(targetDeck, nextTrackUrl, nextTrackBPM);
      
      // ═══════════════════════════════════════════════════════════════
      // CRITICAL: Restore source deck volume IMMEDIATELY after load
      // This prevents the micro-cut caused by updateCrossfaderVolumes()
      // ═══════════════════════════════════════════════════════════════
      if (sourceAudio && !sourceAudio.paused) {
        sourceAudio.volume = sourceVolumeBeforeLoad;
        console.log(`✅ Source Deck ${sourceDeck} volume restored to ${Math.round(sourceVolumeBeforeLoad * 100)}%`);
      }
      
      // Get the newly loaded audio
      targetAudio = targetDeck === 'A' ? this.deckA : this.deckB;
    }
    
    if (!targetAudio) {
      console.error('❌ Failed to load track on target deck');
      this.isCrossfading = false;
      return false;
    }
    
    // CRITICAL: Wait for target track to be ready before starting crossfade
    // This prevents stuttering/gaps if track is still loading
    if (targetAudio.readyState < 2) { // < HAVE_CURRENT_DATA
      console.log('⏳ Waiting for target track to be ready...');
      await new Promise((resolve) => {
        const checkReady = () => {
          if (targetAudio.readyState >= 2) {
            console.log('✅ Target track ready!');
            resolve();
          } else {
            setTimeout(checkReady, 50);
          }
        };
        checkReady();
      });
    }
    
    // ═══════════════════════════════════════════════════════════════
    // SOLUTION FINALE: Ne PAS démarrer target AVANT le crossfade!
    // ═══════════════════════════════════════════════════════════════
    // On va démarrer le target deck PENDANT le premier step du fade
    // Cela évite toute interruption perceptible de la source
    
    // Préparer le target deck (volume 0, mais PAS encore play())
    targetAudio.volume = 0;
    
    const t2 = performance.now();
    // Preparing crossfade
    
    // Perform progressive crossfade with smooth equal-power curve
    const steps = Math.max(100, this.crossfadeDuration * 30); // 30 steps per second for ultra-smooth pro crossfade
    const interval = (this.crossfadeDuration * 1000) / steps;
    
    const t3 = performance.now();
    // Crossfade loop configured
    
    // Calculate final target volumes for restoration after crossfade
    const finalCrossfaderPos = targetDeck === 'A' ? -1 : 1;
    const sourceDeckVol = sourceDeck === 'A' ? this.deckAVolume : this.deckBVolume;
    const targetDeckVol = targetDeck === 'A' ? this.deckAVolume : this.deckBVolume;
    
    // ═══════════════════════════════════════════════════════════════
    // CRITICAL: Source reste à son volume actuel pendant tout le setup
    // ═══════════════════════════════════════════════════════════════
    const initialSourceVolume = sourceAudio ? sourceAudio.volume : 1.0;
    
    const t4 = performance.now();
    // Fade started
    
    // ═══════════════════════════════════════════════════════════════
    // CRITICAL FIX: Exécuter le PREMIER STEP IMMÉDIATEMENT (synchrone)
    // ═══════════════════════════════════════════════════════════════
    // Problème: setInterval attend ~33ms avant le 1er call, mais React setState
    // bloque le thread pendant 100-150ms, causant micro-coupure perceptible
    // Solution: Premier step MAINTENANT, puis setInterval pour le reste
    
    let step = 0;
    let targetStarted = false;
    
    // ═══════════════════════════════════════════════════════════════
    // SOLUTION ULTIME: Tout dans le MÊME cycle synchrone
    // ═══════════════════════════════════════════════════════════════
    // Ordre critique pour zéro délai perceptible:
    // 1. Set volumes AVANT play()
    // 2. play() (fire-and-forget, async)
    // 3. Volumes déjà appliqués → pas de "trou"
    
    const tStep0 = performance.now();
    // Crossfade step 0
    
    // Calculer les premiers volumes du fade (step 1/150)
    const initialProgress = 1 / steps;  
    const initialFadeOut = Math.cos((initialProgress * Math.PI) / 2);  // Equal-power
    const initialFadeIn = Math.sin((initialProgress * Math.PI) / 2);
    
    // ═══════════════════════════════════════════════════════════════
    // PROFESSIONAL DJ FEATURE: Intro Skip for crossfade
    // ═══════════════════════════════════════════════════════════════
    // Start new track at 8s to avoid empty intro during crossfade
    if (targetAudio && targetAudio.currentTime < 2 && targetAudio.duration > 30) {
      const introSkipSeconds = 8;
      targetAudio.currentTime = introSkipSeconds;
      // Intro skip applied
    }
    
    // ORDRE CRITIQUE: Volumes AVANT play()
    if (targetAudio) {
      targetAudio.volume = initialFadeIn;  // Volume déjà appliqué!
      // Target volume set
    }
    
    if (sourceAudio && !sourceAudio.paused) {
      sourceAudio.volume = initialFadeOut;  // Fade démarre maintenant!
      // Source fade started
    }
    
    // PUIS démarrer target (async, fire-and-forget)
    if (targetAudio) {
      targetAudio.play().catch(error => {
        console.error('❌ Error starting target:', error);
      });
      targetStarted = true;
      // Target deck playing
    }
    
    step = 1; // Step 1 déjà fait, continuer à 2
    
    // RAF loop starting
    
    // ═══════════════════════════════════════════════════════════════
    // CRITICAL: Use requestAnimationFrame instead of setInterval
    // ═══════════════════════════════════════════════════════════════
    // rAF is synchronized with browser rendering, causing less audio interruption
    let lastStepTime = performance.now();
    let fadeAnimationId = null;
    
    const fadeStep = () => {
      const now = performance.now();
      const elapsed = now - lastStepTime;
      
      // Only advance if enough time has passed (33.3ms per step)
      if (elapsed >= interval) {
        step++;
        lastStepTime = now;
        
        if (step > steps) {
          cancelAnimationFrame(fadeAnimationId);
          return;
        }
        
        const progress = step / steps;
        
        // ═══════════════════════════════════════════════════════════════
        // PROFESSIONAL EQUAL-POWER CROSSFADE (Constant Power Guarantee)
        // ═══════════════════════════════════════════════════════════════
        // Using trigonometric equal-power curve for DJ-quality transition
        // Mathematical guarantee: cos²(x) + sin²(x) = 1 (constant perceived loudness)
        const fadeOutPower = Math.cos((progress * Math.PI) / 2); // 1.0 → 0.0 (smooth decay)
        const fadeInPower = Math.sin((progress * Math.PI) / 2);   // 0.0 → 1.0 (smooth rise)
        
        // ═══════════════════════════════════════════════════════════════
        // Apply PURE equal-power gains during crossfade
        // ═══════════════════════════════════════════════════════════════
        if (sourceAudio && !sourceAudio.paused) {
          sourceAudio.volume = Math.max(0, Math.min(1, fadeOutPower));
          // Update the deck volume property for UI display
          if (sourceDeck === 'A') {
            this.deckAVolume = fadeOutPower;
          } else {
            this.deckBVolume = fadeOutPower;
          }
        }
        
        if (targetAudio && !targetAudio.paused) {
          targetAudio.volume = Math.max(0, Math.min(1, fadeInPower));
          // Update the deck volume property for UI display
          if (targetDeck === 'A') {
            this.deckAVolume = fadeInPower;
          } else {
            this.deckBVolume = fadeInPower;
          }
        }
        
        // Smoothly move crossfader position for visual feedback
        const startPos = sourceDeck === 'A' ? -1 : 1;
        const endPos = sourceDeck === 'A' ? 1 : -1;
        this.crossfaderPosition = startPos + (endPos - startPos) * progress;
        
        // Notify UI of volume changes for real-time display
        if (this.onDeckChange) {
          this.onDeckChange(this.getDecksInfo());
        }
        
        if (step >= steps) {
          cancelAnimationFrame(fadeAnimationId);
          
          // Crossfade complete
          
          // Pause and reset source deck
          if (sourceAudio) {
            sourceAudio.pause();
            sourceAudio.currentTime = 0;
            sourceAudio.volume = 0;
            console.log(`⏸️ Deck ${sourceDeck} stopped and reset to 0:00`);
          }
          
          // Switch active deck references
          this.activeDeck = targetDeck;
          this.currentAudio = targetAudio;
          this.crossfaderPosition = finalCrossfaderPos;
          
          // ═══════════════════════════════════════════════════════════════
          // POST-CROSSFADE VOLUME RESTORATION
          // ═══════════════════════════════════════════════════════════════
          // Restore proper volumes based on crossfader, master, and individual deck settings
          // This ensures the new active track plays at the correct final volume
          this.updateCrossfaderVolumes();
          
          console.log(`🔊 Volume restored - Deck ${targetDeck} at ${Math.round(targetAudio.volume * 100)}%`);
          
          this.isCrossfading = false;
          
          // Notify deck change for UI updates
          if (this.onDeckChange) {
            this.onDeckChange(this.getDecksInfo());
          }
          
          // ═══════════════════════════════════════════════════════════════
          // INTELLIGENT PRELOADING: Load next track on free deck
          // ═══════════════════════════════════════════════════════════════
          if (queueForPreload && queueForPreload.length > 0) {
            const nextTrackToPreload = queueForPreload[0];
            console.log(`🔄 Auto-preloading next track on free Deck ${sourceDeck}: ${nextTrackToPreload.title || 'Unknown'}`);
            
            if (this.onTrackPreloaded) {
              this.onTrackPreloaded(sourceDeck, nextTrackToPreload);
            }
          } else {
            console.log(`ℹ️ Queue empty - No track to preload on Deck ${sourceDeck}`);
          }
          return;
        }
      }
      
      // Schedule next frame
      fadeAnimationId = requestAnimationFrame(fadeStep);
    };
    
    // Start the animation loop
    fadeAnimationId = requestAnimationFrame(fadeStep);
    
    const tReturn = performance.now();
    // Crossfade return
    
    return true;
  }
}

/**
 * Format seconds to MM:SS
 */
export function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
